<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('super-firmware')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Upload Firmware</h1>
        </header>

        <section class="module__body">
            <form @submit.prevent="uploadFirmware">
                <div class="form-group">
                    <label for="version">Version</label>
                    <input
                        v-model="version"
                        data-test-id="version-field"
                        type="text"
                        maxlength="32"
                        pattern="^\d{1,10}\.\d{1,10}\.\d{1,10}$"
                        :required="true"
                    />
                    <div class="help">Version must be in the format #.#.#</div>
                </div>

                <div class="form-group">
                    <slider-toggle-field
                        name="isPremium"
                        label="Premium Capable"
                        data-test-id="firmware-premium-toggle"
                        :value="is_premium"
                        @update="is_premium = $event"
                    ></slider-toggle-field>
                </div>

                <div
                    v-for="(architecture, index) in requiredArchitectures"
                    :key="index"
                    class="form-group"
                >
                    <file-upload
                        url="firmware"
                        accept=".bin"
                        :required="true"
                        :label="`${architecture.type.toUpperCase()} Binary`"
                        :data-test-id="`file-upload-${architecture.type}-${firmwareFileTypes.RELEASE}`"
                        @error="
                            (formErrorResponse) =>
                                handleUploadError(architecture.type, firmwareFileTypes.RELEASE, formErrorResponse)
                        "
                        @success="
                            (filenames) => tempUploadComplete(architecture.type, firmwareFileTypes.RELEASE, filenames)
                        "
                        @upload-started="uploadStarted(architecture.type, firmwareFileTypes.RELEASE)"
                    />
                </div>

                <template v-if="is_premium">
                    <div
                        v-for="(architecture, index) in requiredArchitectures"
                        :key="index"
                        class="form-group"
                    >
                        <file-upload
                            url="firmware/build"
                            accept=".elf"
                            :data-test-id="`file-upload-${architecture.type}-${firmwareFileTypes.BUILD}`"
                            :required="true"
                            :label="`${architecture.type.toUpperCase()} ELF`"
                            @error="
                                (formErrorResponse) =>
                                    handleUploadError(architecture.type, firmwareFileTypes.BUILD, formErrorResponse)
                            "
                            @success="
                                (filenames) => tempUploadComplete(architecture.type, firmwareFileTypes.BUILD, filenames)
                            "
                            @upload-started="uploadStarted(architecture.type, firmwareFileTypes.BUILD)"
                        />
                    </div>
                </template>

                <div class="form-group">
                    <label for="protocol">Protocol</label>
                    <select
                        id="protocol"
                        v-model="protocol"
                        required
                    >
                        <option value="ag1.0">ag1.0</option>
                        <option value="ag1.1">ag1.1</option>
                        <option value="ag1.2">ag1.2</option>
                    </select>
                </div>

                <div class="form-group">
                    <label for="release_date">Release Date</label>
                    <input
                        id="release_date"
                        v-model="release_date"
                        type="date"
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="description">Description</label>
                    <textarea
                        id="description"
                        v-model="description"
                        required
                    />
                </div>

                <div class="form-group">
                    <slider-toggle-field
                        name="isPremium"
                        label="Show Optional Architectures"
                        data-test-id="show-optional-architectures-toggle"
                        :value="show_optional_architectures"
                        @update="show_optional_architectures = $event"
                    ></slider-toggle-field>
                </div>

                <template v-if="show_optional_architectures">
                    <!-- eslint-disable vue/no-v-for-template-key -->
                    <template
                        v-for="(architecture, index) in optionalArchitectures"
                        :key="index"
                    >
                        <div class="form-group">
                            <file-upload
                                url="firmware"
                                accept=".bin"
                                :label="`${architecture.type.toUpperCase()} Binary`"
                                :data-test-id="`file-upload-${architecture.type}-${firmwareFileTypes.RELEASE}`"
                                :required="isOptionalArchitectureReleaseRequired(architecture)"
                                @error="
                                    (formErrorResponse) =>
                                        handleUploadError(
                                            architecture.type,
                                            firmwareFileTypes.RELEASE,
                                            formErrorResponse
                                        )
                                "
                                @success="
                                    (filenames) =>
                                        tempUploadComplete(architecture.type, firmwareFileTypes.RELEASE, filenames)
                                "
                                @upload-started="uploadStarted(architecture.type, firmwareFileTypes.RELEASE)"
                            />
                        </div>

                        <div
                            v-if="is_premium"
                            class="form-group"
                        >
                            <file-upload
                                url="firmware/build"
                                accept=".elf"
                                :data-test-id="`file-upload-${architecture.type}-${firmwareFileTypes.BUILD}`"
                                :required="isOptionalArchitectureBuildRequired(architecture)"
                                :label="`${architecture.type.toUpperCase()} ELF`"
                                @error="
                                    (formErrorResponse) =>
                                        handleUploadError(architecture.type, firmwareFileTypes.BUILD, formErrorResponse)
                                "
                                @success="
                                    (filenames) =>
                                        tempUploadComplete(architecture.type, firmwareFileTypes.BUILD, filenames)
                                "
                                @upload-started="uploadStarted(architecture.type, firmwareFileTypes.BUILD, filenames)"
                            />
                        </div>
                    </template>
                    <!-- eslint-enable vue/no-v-for-template-key -->
                </template>

                <form-errors :response="errorResponse" />

                <div class="form-group">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :class="{ active: submitted }"
                    >
                        <div>Upload</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import FileUpload from '@/components/FileUpload/index.vue'
import FormErrors from '@/components/FormErrors.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import SliderToggleField from '@/components/SliderToggleField.vue'
import firmwareFileTypes from '@/constants/firmware-file-types'
import architectures from '@/constants/firmware-architectures'

export default {
    components: {
        FileUpload,
        FormErrors,
        SliderToggleField,
    },

    mixins: [canToast, hasBackButton],

    data() {
        const filenames = {}

        return {
            architectures,
            filenames,
            protocol: 'ag1.0',
            release_date: null,
            description: null,
            errorResponse: null,
            submitted: false,
            is_premium: true,
            show_optional_architectures: false,
            version: null,
        }
    },

    computed: {
        allFilesUploaded() {
            const requiredIsValid = this.requiredArchitectures.every((arch) => {
                const files = this.filenames[arch.type]

                const isReleaseUploaded = this.isValidUploadData(files?.[firmwareFileTypes.RELEASE])
                const isBuildUploaded = this.isValidUploadData(files?.[firmwareFileTypes.BUILD])

                return isReleaseUploaded && (!this.is_premium || isBuildUploaded)
            })

            const optionalIsValid = this.optionalArchitectures.every((arch) => {
                const files = this.filenames[arch.type]

                const isReleaseUploaded = this.isValidUploadData(files?.[firmwareFileTypes.RELEASE])
                const isBuildUploaded = this.isValidUploadData(files?.[firmwareFileTypes.BUILD])

                const releaseRequired = this.isOptionalArchitectureReleaseRequired(arch)
                const buildRequired = this.isOptionalArchitectureBuildRequired(arch)

                return (!releaseRequired || isReleaseUploaded) && (!buildRequired || isBuildUploaded)
            })

            return requiredIsValid && optionalIsValid
        },

        firmwareFileTypes() {
            return firmwareFileTypes
        },

        requiredArchitectures() {
            return this.architectures.filter((arch) => arch.required)
        },

        optionalArchitectures() {
            return this.architectures.filter((arch) => !arch.required)
        },
    },

    watch: {
        show_optional_architectures(newValue) {
            if (!newValue) {
                this.optionalArchitectures.forEach((arch) => {
                    delete this.filenames[arch.type]
                })
            }
        },

        is_premium(newValue) {
            if (!newValue) {
                this.architectures.forEach((arch) => {
                    if (this.filenames[arch.type] !== undefined) {
                        if (this.filenames[arch.type][firmwareFileTypes.RELEASE] === undefined) {
                            delete this.filenames[arch.type]
                        } else {
                            this.filenames[arch.type][firmwareFileTypes.BUILD] = {}
                        }
                    }
                })
            }
        },
    },

    methods: {
        isOptionalArchitectureBuildRequired(arch) {
            // build file is required if a release has been uploaded for the same architecture
            return (
                this.is_premium &&
                this.isValidUploadData(this.filenames[arch.type]?.[firmwareFileTypes.RELEASE]) === true
            )
        },
        isOptionalArchitectureReleaseRequired(arch) {
            // release file is required if a build has been uploaded for the same architecture
            return this.isValidUploadData(this.filenames[arch.type]?.[firmwareFileTypes.BUILD]) === true
        },
        isValidUploadData(data) {
            return Boolean(data?.original_filename && data?.temp_filename)
        },
        tempUploadComplete(arch, type, filenames) {
            const { original, temp } = filenames

            if (this.filenames[arch][type] !== undefined) {
                this.filenames[arch][type].original_filename = original
                this.filenames[arch][type].temp_filename = temp
            }

            this.errorResponse = null
        },

        uploadStarted(arch, type) {
            // when upload is started, add placeholders
            if (!this.filenames[arch]) {
                this.filenames[arch] = {
                    [type]: {},
                }
            } else if (!this.filenames[arch][type]) {
                this.filenames[arch][type] = {}
            }
        },

        uploadFirmware() {
            if (this.submitted) {
                return
            }

            if (!this.allFilesUploaded) {
                this.errorResponse = {
                    data: {
                        errors: {
                            Files: ['Please wait until all files have been uploaded.'],
                        },
                    },
                }
                return
            }

            this.submitted = true
            this.$http
                .post('firmware', {
                    uploaded_files: this.filenames,
                    protocol: this.protocol,
                    release_date: this.release_date,
                    description: this.description,
                    is_premium: Number(this.is_premium),
                    version: this.version,
                })
                .then(() => {
                    this.toast('Firmware successfully uploaded!')
                    this.$router.push('/firmware')
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.submitted = false
                })
        },

        handleUploadError(arch, type, uploadError) {
            this.errorResponse = uploadError

            delete this.filenames[arch][type]

            if (Object.keys(this.filenames[arch]).length === 0) {
                delete this.filenames[arch]
            }
        },
    },
}
</script>
